import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import SoftAlertBox from 'src/components/SoftAlertBox/SoftAlertBox'
import { UserOption } from 'src/components/UserDropdown/UserDropdown'
import VirtualCardForm, {
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { ImageNames, VirtualCardTypes } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { UserListModel } from 'src/models/user.models'
import {
  ApprovalStatus,
  VirtualCardSecureModel,
} from 'src/models/virtualCard.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import { virtualCardsAllCardsUrl } from 'src/navigation/urls'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import { selectSelectedVirtualCardIdsFromState } from 'src/redux/selectors/data/virtualCards.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PutVirtualCardRequest } from 'src/requests/putVirtualCard.request'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { css } from 'styled-components'

interface UpdateVirtualCardGuestAccessContentProps {
  cardApproverId: string | undefined
  virtualCard: VirtualCardSecureModel
  virtualCardPermissions: VirtualCardPermissionsModel
  recipient: UserListModel
  cardholder: UserListModel
  userOptions: UserOption[]
  isFormDisabled: boolean
  labelCreditCardsAsAccounts: boolean
  creditCardOptions: CreditCardOption[]
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  puttingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  putVirtualCardAndOpenToastAndCloseFlyout: (
    id: string,
    body: Partial<PutVirtualCardRequest>,
    openToast?: boolean,
  ) => void
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  negativeButton?: {
    loading: boolean
    text: React.ReactElement | string
    onClick: () => void
  }
  creditCardInfiniteScrollProps?: CreditCardDropdownInfiniteScrollProps
  showSoftAlert?: boolean
}

const UpdateVirtualCardGuestAccessContent: React.FC<
  UpdateVirtualCardGuestAccessContentProps
> = (props) => {
  const queryParams = useSelector(selectQueryParams)
  const selectedVirtualCardIdsFromRedux = useSelector(
    selectSelectedVirtualCardIdsFromState,
  )
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const userId = useSelector(selectUserIdFromState)
  const cardTypeGuestImage = useColorOrGrayscaleImage(ImageNames.CardTypeGuest)

  const isCurrentUserCardApprover = userId === props.cardApproverId
  const baseFlatFields = useMemo(
    (): VirtualCardFlatFormValues => ({
      displayName: props.virtualCard.displayName,
      userEmails: props.virtualCardPermissions.canUpdateVirtualCard
        ? [props.recipient.email]
        : [props.cardholder.email],
      creditCardId: props.virtualCard.creditCardId,
      balanceDollars: props.virtualCard.balance,
      notes: props.virtualCard.notes,
      validToMoment: props.virtualCard.validToTimezonedMoment,
      currency: props.virtualCard.currency,
    }),
    [props.virtualCard, props.recipient, props.virtualCardPermissions],
  )

  // const newRequestInitialCreditCardId = props.creditCardOptions.length === 1 && props.virtualCard.canUpdateVirtualCard
  //   ? props.creditCardOptions[0].value
  //   : queryParams['creditCardId']
  const newRequestInitialCreditCardId =
    typeof queryParams.creditCardId === 'string'
      ? queryParams.creditCardId
      : undefined // TODO
  const initialFlatFields = useMemo(
    (): VirtualCardFlatFormValues => ({
      displayName: props.virtualCard.displayName,
      userEmails: props.virtualCardPermissions.canUpdateVirtualCard
        ? [props.recipient.email]
        : [props.cardholder.email],
      creditCardId:
        props.virtualCard.creditCardId || newRequestInitialCreditCardId,
      balanceDollars:
        props.virtualCard.pending?.balance || props.virtualCard.balance,
      notes: props.virtualCard.notes,
      validToMoment:
        props.virtualCard.pending?.validToTimezonedMoment(
          props.virtualCard.timezone,
        ) || props.virtualCard.validToTimezonedMoment,
    }),
    [
      props.virtualCard,
      props.recipient,
      newRequestInitialCreditCardId,
      props.virtualCardPermissions,
    ],
  )

  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
  ): void => {
    props.putVirtualCardAndOpenToastAndCloseFlyout(props.virtualCard.id, {
      displayName: flatFields.displayName,
      expenseDetails: Object.entries(expenseDetails)
        .filter((keyVal) => !!keyVal[1])
        .map(([key, val]) => ({ categoryId: key, labelId: val })),
      notes: flatFields.notes,
      balanceCents:
        flatFields.balanceDollars !== undefined
          ? toCents(flatFields.balanceDollars)
          : undefined,
      currency: flatFields.currency,
      recurs: false,
      receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
      validTo:
        flatFields.validToMoment !== undefined
          ? flatFields.validToMoment.format('YYYY-MM-DD')
          : undefined,
      pendingRevisionId: props.virtualCard.pending?.id,
    })
  }

  return (
    <ActionRightFlyoutContent
      title={props.virtualCard.updateFlyoutTitle(props.virtualCardPermissions)}
      specificElements={{
        subtitle: <Trns source={'Guest Card'} />,
        imageSrc: cardTypeGuestImage,
      }}
      cardChangeNotAllowed={
        !props.virtualCard.isPending &&
        (props.virtualCard.features.singleExactPay ||
          props.virtualCard.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval)
      }
    >
      <VirtualCardForm
        topText={
          <>
            {matchPath(
              calculatedBackgroundLocation.pathname,
              virtualCardsAllCardsUrl(),
            ) &&
              selectedVirtualCardIdsFromRedux !== undefined &&
              selectedVirtualCardIdsFromRedux?.length > 1 && (
                <SoftAlertBox
                  css={css`
                    padding: 16px 20px;
                    margin-bottom: 20px;
                  `}
                >
                  <Trns
                    source={
                      'You can only edit virtual cards you have created or have access to as a budget user'
                    }
                  />
                </SoftAlertBox>
              )}
            {!isCurrentUserCardApprover
              ? props.virtualCard.updateFormTopText(
                  props.virtualCardPermissions,
                )
              : undefined}
          </>
        }
        virtualCard={props.virtualCard}
        initialFlatFields={initialFlatFields}
        updateProps={{
          baseFlatFields: baseFlatFields,
          timezone: props.virtualCard.timezone,
        }}
        buttonText={props.virtualCard.updateFormButtonText(
          props.virtualCardPermissions,
          isCurrentUserCardApprover,
        )}
        onSuccessfulSubmit={onSuccessfulSubmit}
        userOptions={props.userOptions}
        loading={props.puttingVirtualCard}
        userFieldLabel={
          props.virtualCardPermissions.canUpdateVirtualCard ? (
            <Trns source={'Recipient'} />
          ) : (
            <Trns source={'Request From'} />
          )
        }
        recipientNamesEditable={false}
        recurrenceEditable={false}
        notesEditable={true}
        amountProps={{
          amountEditable: true,
        }}
        creditCardEditable={
          props.virtualCardPermissions.canUpdateVirtualCard &&
          props.virtualCard.isPending
        }
        creditCardProps={
          !props.virtualCardPermissions.canUpdateVirtualCard
            ? undefined
            : {
                isFormDisabled: props.isFormDisabled,
                creditCardOptions: props.creditCardOptions,
                labelCreditCardsAsAccounts: props.labelCreditCardsAsAccounts,
              }
        }
        attachmentsProps={{
          receiptAttachments: props.receiptAttachments,
          uploadingAttachmentThumbnails: props.uploadingAttachmentThumbnails,
          uploadAttachments: props.uploadAttachments,
          deleteAttachment: props.deleteAttachment,
        }}
        negativeButton={props.negativeButton}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
        typeofCard={VirtualCardTypes.guestAccess}
        showCardApprovalSoftAlert={props.showSoftAlert}
        awaitingFinalApproval={
          props.virtualCard.awaitingApprovalStatus ===
          ApprovalStatus.AwaitingApproverRuleApproval
        }
        awaitingFundingSourceOwnerApproval={
          props.virtualCard.awaitingApprovalStatus ===
          ApprovalStatus.AwaitingCreditCardUserApproval
        }
        cardApproverId={props.cardApproverId}
      />
    </ActionRightFlyoutContent>
  )
}

export default UpdateVirtualCardGuestAccessContent
